import { useCart } from '@shopify/hydrogen-react';
import { CartCost, CartLine } from '@shopify/hydrogen-react/storefront-api-types';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { ReactComponent as IconMinus } from '../../assets/icons/icon-minus.svg';
import { ReactComponent as IconPlus } from '../../assets/icons/icon-plus.svg';
import { NotificationMessages } from '../../common/notificationMessages';
import FlashMessages from '../../components/ui/FlashMessages';
import { updateCartItems } from '../../utils/storefront/cart/actions';
import { flatResponseEdges } from '../../utils/storefront/flatResponseEdges';
import { handleStorefrontClientError } from '../../utils/storefront/storefrontClientErrorHandler';

interface Props {
  currentQuantity?: number;
  disableCheckout: (value: boolean) => void;
  itemId: string;
  maxQuantity?: number;
  onChange: (cartLines: CartLine[], cartCost: CartCost, oldValue: number, newValue: number) => void;
}

const Quantity: React.FC<Props> = ({
  currentQuantity,
  disableCheckout,
  maxQuantity,
  itemId,
  onChange,
}) => {
  const [maxLimit] = useState<number>(maxQuantity && maxQuantity > 0 ? maxQuantity : 20);
  const [disableAdding, setDisableAdding] = useState<boolean>(false);
  const [disableActions, setDisableActions] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(currentQuantity || 1);
  const { id, linesUpdate } = useCart();

  useEffect(() => {
    setQuantity(currentQuantity || 1);
  }, [currentQuantity]);

  const update = (cartId: string, lineItemId: string, oldValue: number, newValue: number) => {
    disableCheckout(true);
    setDisableActions(true);
    setDisableAdding(true);
    updateCartItems(cartId, [
      {
        id: lineItemId,
        quantity: newValue,
      },
    ])
      .then(results => {
        if (newValue > maxLimit) {
          FlashMessages.warn(NotificationMessages.MAX_QUANTITY_REACHED);
          return;
        }
        linesUpdate([
          {
            id: lineItemId,
            quantity: newValue,
          },
        ]);
        results?.cart?.lines?.edges &&
          results?.cart &&
          onChange(
            flatResponseEdges(results?.cart?.lines?.edges) as CartLine[],
            results.cart.cost as CartCost,
            oldValue,
            newValue
          );
        setQuantity(newValue);
        setDisableAdding(false);
      })
      .catch(e => {
        handleStorefrontClientError(e, NotificationMessages.DEFAULT_ERROR);
      })
      .finally(() => {
        disableCheckout(false);
        setDisableActions(false);
      });
  };

  const handleDecrement = () => {
    if (!id || quantity <= 1) {
      return;
    }
    update(id, itemId, quantity, quantity - 1);
  };

  const handleIncrement = () => {
    if (!id) {
      return;
    }
    update(id, itemId, quantity, quantity + 1);
  };

  return (
    <div className="product-quantity">
      <div className="form-group small">
        <div className="form-field icon-before icon-after">
          <span className="icon icon-before">
            <IconMinus
              className={classNames({
                'product-quantity-action': true,
                disabled: quantity === 1 || disableActions,
              })}
              onClick={() => {
                quantity > 1 && !disableActions && handleDecrement();
              }}
            />
          </span>
          <input
            className="small"
            disabled={true}
            max={20}
            name="quantity"
            type="number"
            value={quantity}
            readOnly
          />
          <span className="icon icon-after">
            <IconPlus
              className={classNames({
                'product-quantity-action': true,
                disabled: disableAdding || disableActions,
              })}
              onClick={() => {
                (!disableAdding || !disableActions) && handleIncrement();
              }}
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default Quantity;
